import { memo, useCallback, useEffect, useRef, useState, type JSX } from 'react'

import { ImageObject } from '@nativewaves/nw-cloud-sdk/content'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { Table as ReactTableType, RowSelectionState, Row } from '@tanstack/react-table'
import { VirtualItem } from '@tanstack/react-virtual'
import { pick } from 'lodash-es'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { Dialog } from '@shared/components/MaterialUIEnhancements/Dialog'
import { SearchBar } from '@shared/components/SearchBar'
import { SelectionToolbar, Table } from '@shared/components/Table'
import { ActionStrip, BasicListOperation } from '@shared/layouts'
import {
  useColumns,
  NewDialog,
  RowActions,
  useAsyncContentCaching,
  SuccessPanelActions,
  SelectionToolbarActions,
  LifecyclePolicyDialog,
} from 'domains/Workspaces/Images'
import { BodyCellLink } from 'layouts/BodyCellLink'
import { rawRoute } from 'pages/NWPlatform/Workspaces/Workspace/Images/Image/Routing'
import { imagesOverviewRoute, imagesRoute } from 'pages/NWPlatform/Workspaces/Workspace/Images/Routing'
import { imageObjectQueryKeys } from 'services/queryKeys'

const OverviewRaw = (): JSX.Element => {
  const { t } = useTranslation(['common', 'domain', 'entity'])
  const { enqueueSnackbar } = useSnackbar()
  const queryClient = useQueryClient()

  const navigate = useNavigate({ from: imagesOverviewRoute.fullPath })
  const routeParams = imagesOverviewRoute.useParams()
  const { query, groups, excludeGrouped } = imagesOverviewRoute.useSearch()

  const tableInstance = useRef<ReactTableType<ImageObject>>(undefined)
  const virtualizerInstanceRef = useRef<VirtualItem[]>(undefined)

  const [newDialogOpen, setNewDialogOpen] = useState(false)
  const [loadingRows, setLoadingRows] = useState<string[]>([])
  const [objectData, setObjectData] = useState<ImageObject | ImageObject[] | undefined>(undefined)

  const { workspaceQuery, imagesQuery } = useAsyncContentCaching({
    virtualItems: virtualizerInstanceRef.current,
    setLoadingRows,
  })

  const columns = useColumns()

  const handleSuccess = useCallback(
    (data: ImageObject) => {
      enqueueSnackbar(`${t('domain:Workspace.Image.imageCreated', { name: data.name })}`, {
        description: `${t('domain:Workspace.Image.imageCreatedDescription')}`,
        variant: 'panel',
        persist: true,
        icon: 'success',
        Actions: (props) => <SuccessPanelActions {...props} {...routeParams} imageId={data.id} />,
      })
    },
    [enqueueSnackbar, routeParams, t],
  )

  const handleLifecyclePolicyClick = useCallback((selectedRows: Row<ImageObject>[]) => {
    const objectData = selectedRows.map((row) => row.original)
    setObjectData(objectData)
  }, [])

  useEffect(() => {
    if (imagesQuery.isSuccess) {
      tableInstance.current?.setRowSelection((old) =>
        Object.keys(old).reduce((acc, newItem) => {
          const newItemFound = imagesQuery.data?.flat.find((image) => image.id === newItem)
          if (newItemFound) {
            return { ...acc, [newItem]: true }
          }
          return acc
        }, {} as RowSelectionState),
      )
    }
  }, [imagesQuery.data?.flat, imagesQuery.isSuccess])

  return (
    <>
      <ActionStrip>
        <SearchBar
          placeholder={t('domain:Workspace.Image.searchImage')}
          onSearch={({ search }) => navigate({ search: (prev) => ({ ...prev, query: search }) })}
          defaultValue={query}
        />

        <BasicListOperation
          refreshAction={useCallback(() => {
            queryClient.cancelQueries({ queryKey: imageObjectQueryKeys.lists() })
            queryClient.invalidateQueries({
              queryKey: imageObjectQueryKeys.list({
                environmentId: routeParams.environmentId,
                query,
                groups,
                excludeGrouped,
              }).queryKey,
            })
          }, [query, queryClient, routeParams.environmentId, groups, excludeGrouped])}
          createAction={useCallback(() => {
            if (workspaceQuery.isSuccess) {
              setNewDialogOpen(true)
              tableInstance.current?.resetRowSelection()
            }
          }, [workspaceQuery.isSuccess])}
        />
      </ActionStrip>

      <Table
        getTableInstance={(instance) => (tableInstance.current = instance)}
        size={48}
        data={imagesQuery.data?.flat ?? []}
        columns={columns}
        isInitialLoadingGlobal={imagesQuery.isLoading}
        isInitialLoadingByRowByID={loadingRows.reduce((acc, row) => ({ ...acc, [row]: [] }), {})}
        enableLinking
        LinkProps={(row, content, cell) => (
          <BodyCellLink
            sx={[!!cell.column.columnDef.meta?.align && { justifyContent: cell.column.columnDef.meta.align }]}
            from={imagesRoute.fullPath}
            to={rawRoute.to}
            params={{ imageId: row.original.id }}
          >
            {content}
          </BodyCellLink>
        )}
        enableInfinity
        infinityProps={pick(imagesQuery, ['hasNextPage', 'isFetchingNextPage', 'fetchNextPage'])}
        enableVirtualization
        virtualizedProps={{
          getVirtualItems: (instance) => (virtualizerInstanceRef.current = instance),
        }}
        ActionMenuProps={(row) => ({
          title: row.original.name,
          entity: t('entity:content.image', { count: 1 }),
        })}
        RowActions={(props) => <RowActions onClick={(row) => setObjectData(row.original)} {...props} />}
        renderSelectionToolbar={({ selectedRows }) => (
          <SelectionToolbar
            selectedRows={selectedRows}
            title={t('entity:content.image', { count: 2 })}
            discardSelection={() => tableInstance.current?.resetRowSelection()}
            Actions={
              tableInstance.current && (
                <SelectionToolbarActions
                  onLifecyclePolicyClick={() => handleLifecyclePolicyClick(selectedRows)}
                  selectedRows={selectedRows}
                  tableInstance={tableInstance.current}
                />
              )
            }
          />
        )}
        tableOptions={{
          getRowId: (image) => image.id,
        }}
      />
      {workspaceQuery.isSuccess && (
        <Dialog open={newDialogOpen} onClose={() => setNewDialogOpen(false)}>
          <NewDialog onSuccess={handleSuccess} repositoryId={workspaceQuery.data.contentRepositoryId} />
        </Dialog>
      )}

      <Dialog open={!!objectData} onClose={() => setObjectData(undefined)}>
        {objectData ? (
          <LifecyclePolicyDialog objectData={objectData} onSuccess={() => tableInstance.current?.resetRowSelection()} />
        ) : null}
      </Dialog>
    </>
  )
}

export const Overview = memo(OverviewRaw)
